import dayjs from 'dayjs'
import Vue from 'vue'
import Vuex from 'vuex'
import createdPersistedState from 'vuex-persistedstate'
import GraphqlAPI from '../utils/GraphqlAPI'
import { graphqlAPIRequester } from '../utils/graphql/GraphqlApiRequester'
import { continueBonusProcessorMutation } from '../utils/graphql/graphqlSchema/Mutations'

Vue.use(Vuex)

const recentCampaignQuery = () => `
  query getRecentCampaign {
    getRecentCampaign {
      campaign {
        id
        name
        start
        end
        is_continuity
        questionnaire_start
        questionnaire_end
        relational_campaign_id
        site_open
        site_close
        is_team
        is_questionnaire
        is_prize
        is_prize_select
        is_app_challenge
        is_learning
        is_continue_bonus
        is_course_select_validation
        max_course_select
        course_deletable_end
        app_deadline_date
        prize_mail_send_date
        is_board
        required_advanced_course_count
        required_total_course_count
        participants
        how_to_join
      }
      option_flags {
        is_team_board
        is_walking_travel
        is_course_pack
        is_before_questionnaire
        is_recommend_course
      }
    }
  }
`
const fetchRecentCampaignInfo = async () => {
  const { data: { getRecentCampaign: data } } = await GraphqlAPI(recentCampaignQuery())
  return data
}

const campaignQuery = (id) => `
  query getCampaign {
    getCampaign(id: "${id}") {
      campaign {
        id
        name
        start
        end
        is_continuity
        questionnaire_start
        questionnaire_end
        relational_campaign_id
        site_open
        site_close
        is_team
        is_questionnaire
        is_prize
        is_prize_select
        is_app_challenge
        is_learning
        is_continue_bonus
        is_course_select_validation
        max_course_select
        course_deletable_end
        app_deadline_date
        prize_mail_send_date
        is_board
        required_advanced_course_count
        required_total_course_count
        participants
        how_to_join
      }
      option_flags {
        is_team_board
        is_walking_travel
        is_course_pack
        is_before_questionnaire
        is_recommend_course
      }
    }
  }
`
const fetchCampaignInfo = async (campaignId) => {
  const { data: { getCampaign: data } } = await GraphqlAPI(campaignQuery(campaignId))
  return data
}

const store = new Vuex.Store({
  strict: true,
  plugins: [createdPersistedState({
    key: 'nskenpo',
    paths: [
      'campaign',
      'campaignOption',
      'relationalCampaign',
      'relationalCampaignOption',
      'emailVerified',
      'registeredCourse',
      'appEntryStatus',
      'beforeQuestionnaireAnswerAt',
      'isMobileApp'
    ],
    storage: window.sessionStorage
  })],
  state: {
    isAuthenticated: false,
    user: {},
    campaign: {},
    campaignOption: {},
    relationalCampaign: {},
    relationalCampaignOption: {},
    emailVerified: false,
    registeredCourse: false,
    appEntryStatus: null,
    continueBonusHistorys: [],
    continueBonus: {},
    beforeQuestionnaireAnswerAt: null,
    isMobileApp: false,
    needsSaveHealthDataFromMobileApp: false,
    needsSaveHealthDataFromMobileAppRelational: false,
    walkingPointListFromMobileApp: [],
    weightListFromMobileApp: [],
    bloodPressureListFromMobileApp: []
  },
  getters: {
    getCampaign (state) {
      return state.campaign
    },
    getCampaignOption (state) {
      return state.campaignOption
    },
    getRelationalCampaign (state) {
      return state.relationalCampaign
    },
    getRelationalCampaignOption (state) {
      return state.relationalCampaignOption
    },
    getEmailVerified (state) {
      return state.emailVerified
    },
    getRegisteredCourse (state) {
      return state.registeredCourse
    },
    getAppEntryStatus (state) {
      return state.appEntryStatus
    },
    getContinueBonusHistorys (state) {
      return state.continueBonusHistorys
    },
    getContinueBonus (state) {
      return state.continueBonus
    },
    getBeforeQuestionnaireAnswerAt (state) {
      return state.beforeQuestionnaireAnswerAt
    },
    getIsMobileApp (state) {
      return state.isMobileApp
    },
    getNeedsSaveHealthDataFromMobileApp (state) {
      return state.needsSaveHealthDataFromMobileApp
    },
    getNeedsSaveHealthDataFromMobileAppRelational (state) {
      return state.needsSaveHealthDataFromMobileAppRelational
    },
    getWalkingPointListFromMobileApp (state) {
      return state.walkingPointListFromMobileApp
    },
    getWeightListFromMobileApp (state) {
      return state.weightListFromMobileApp
    },
    getBloodPressureListFromMobileApp (state) {
      return state.bloodPressureListFromMobileApp
    }
  },
  mutations: {
    isAuthenticated (state, isAuthenticated) {
      state.isAuthenticated = isAuthenticated
    },
    setUser (state, user) {
      state.user = user
    },
    setEmailVerified (state, emailVerified) {
      state.emailVerified = emailVerified
    },
    setCampaign (state, campaign) {
      state.campaign = campaign
    },
    setCampaignOption (state, campaignOption) {
      state.campaignOption = campaignOption
    },
    setRelationalCampaign (state, relationalCampaign) {
      state.relationalCampaign = relationalCampaign
    },
    setRelationalCampaignOption (state, relationalCampaignOption) {
      state.relationalCampaignOption = relationalCampaignOption
    },
    setRegisteredCourse (state, registeredCourse) {
      state.registeredCourse = registeredCourse
    },
    setAppEntryStatus (state, appEntryStatus) {
      state.appEntryStatus = appEntryStatus
    },
    setContinueBonusHistorys (state, continueBonusHistorys) {
      state.continueBonusHistorys = continueBonusHistorys
    },
    setContinueBonus (state, continueBonus) {
      state.continueBonus = continueBonus
    },
    setBeforeQuestionnaireAnswerAt (state, beforeQuestionnaireAnswerAt) {
      state.beforeQuestionnaireAnswerAt = beforeQuestionnaireAnswerAt
    },
    setIsMobileApp (state, isMobileApp) {
      state.isMobileApp = isMobileApp
    },
    setNeedsSaveHealthDataFromMobileApp (state, needsSaveHealthDataFromMobileApp) {
      state.needsSaveHealthDataFromMobileApp = needsSaveHealthDataFromMobileApp
    },
    setNeedsSaveHealthDataFromMobileAppRelational (state, needsSaveHealthDataFromMobileAppRelational) {
      state.needsSaveHealthDataFromMobileAppRelational = needsSaveHealthDataFromMobileAppRelational
    },
    setWalkingPointListFromMobileApp (state, walkingPointListFromMobileApp) {
      state.walkingPointListFromMobileApp = walkingPointListFromMobileApp
    },
    setWeightListFromMobileApp (state, weightListFromMobileApp) {
      state.weightListFromMobileApp = weightListFromMobileApp
    },
    setBloodPressureListFromMobileApp (state, bloodPressureListFromMobileApp) {
      state.bloodPressureListFromMobileApp = bloodPressureListFromMobileApp
    }
  },
  actions: {
    setIsAuthenticated ({ commit }, isAuthenticated) {
      commit('isAuthenticated', isAuthenticated)
    },
    setUser ({ commit }, user) {
      commit('setUser', user)
    },
    setEmailVerified ({ commit }, emailVerified) {
      commit('setEmailVerified', emailVerified)
    },
    setRegisteredCourse ({ commit }, registeredCourse) {
      commit('setRegisteredCourse', registeredCourse)
    },
    setAppEntryStatus ({ commit }, appEntryStatus) {
      commit('setAppEntryStatus', appEntryStatus)
    },
    setBeforeQuestionnaireAnswerAt ({ commit }, beforeQuestionnaireAnswerAt) {
      commit('setBeforeQuestionnaireAnswerAt', beforeQuestionnaireAnswerAt)
    },
    async initCampaign ({ commit }) {
      try {
        const fetchRecentCampaignDataResult = await fetchRecentCampaignInfo()
        commit('setCampaign', fetchRecentCampaignDataResult.campaign)
        commit('setCampaignOption', fetchRecentCampaignDataResult.option_flags ? fetchRecentCampaignDataResult.option_flags : {})
        const relationalCampaignId = fetchRecentCampaignDataResult.campaign.relational_campaign_id
        if (relationalCampaignId) {
          const fetchRelationalCampaignDataResult = await fetchCampaignInfo(relationalCampaignId)
          commit('setRelationalCampaign', fetchRelationalCampaignDataResult.campaign)
          commit('setRelationalCampaignOption', fetchRelationalCampaignDataResult.option_flags ? fetchRelationalCampaignDataResult.option_flags : {})
        }
      } catch (e) {
        console.log(e)
      }
    },
    async setContinueBonusProcessorOutput ({ commit }, { campaignId, type }) {
      const continueBonusApiResponse = await graphqlAPIRequester({ input: { campaignId, type }, func: continueBonusProcessorMutation })
      const continueBonusProcessor = continueBonusApiResponse.continueBonusProcessor
      const ContinueBonusHistorys = continueBonusProcessor.historys.map(history => {
        history.start = dayjs(history.start).format('MM/DD')
        history.end = dayjs(history.end).format('MM/DD')
        return history
      })
      const continueBonus = {
        incentive_requirement: continueBonusProcessor.incentive_requirement,
        per_day: continueBonusProcessor.per_day,
        is_continue_bonus_complete: continueBonusProcessor.is_continue_bonus_complete,
        continue_bonus_point: continueBonusProcessor.continue_bonus_point
      }
      commit('setContinueBonusHistorys', ContinueBonusHistorys)
      commit('setContinueBonus', continueBonus)
      if (type === 'write') {
        return continueBonusProcessor
      }
    },
    setIsMobileApp ({ commit }, isMobileApp) {
      commit('setIsMobileApp', isMobileApp)
    },
    setNeedsSaveHealthDataFromMobileApp ({ commit }, needsSaveHealthDataFromMobileApp) {
      commit('setNeedsSaveHealthDataFromMobileApp', needsSaveHealthDataFromMobileApp)
    },
    setNeedsSaveHealthDataFromMobileAppRelational ({ commit }, needsSaveHealthDataFromMobileAppRelational) {
      commit('setNeedsSaveHealthDataFromMobileAppRelational', needsSaveHealthDataFromMobileAppRelational)
    },
    setWalkingPointListFromMobileApp ({ commit }, walkingPointListFromMobileApp) {
      commit('setWalkingPointListFromMobileApp', walkingPointListFromMobileApp)
    },
    setWeightListFromMobileApp ({ commit }, weightListFromMobileApp) {
      commit('setWeightListFromMobileApp', weightListFromMobileApp)
    },
    setBloodPressureListFromMobileApp ({ commit }, bloodPressureListFromMobileApp) {
      commit('setBloodPressureListFromMobileApp', bloodPressureListFromMobileApp)
    },
    changeCampaign ({ commit }) {
      const _relationalCampaign = this.state.relationalCampaign
      const _relationalCampaignOption = this.state.relationalCampaignOption
      const _campaign = this.state.campaign
      const _campaignOption = this.state.campaignOption
      const _needsSaveHealthDataFromMobileAppRelational = this.state.needsSaveHealthDataFromMobileAppRelational
      const _needsSaveHealthDataFromMobileApp = this.state.needsSaveHealthDataFromMobileApp
      commit('setCampaign', _relationalCampaign)
      commit('setCampaignOption', _relationalCampaignOption)
      commit('setRelationalCampaign', _campaign)
      commit('setRelationalCampaignOption', _campaignOption)
      commit('setAppEntryStatus', null)
      commit('setNeedsSaveHealthDataFromMobileApp', _needsSaveHealthDataFromMobileAppRelational)
      commit('setNeedsSaveHealthDataFromMobileAppRelational', _needsSaveHealthDataFromMobileApp)
    },
    signOut ({ commit }) {
      commit('isAuthenticated', false)
      commit('setUser', {})
      commit('setEmailVerified', false)
      commit('setRegisteredCourse', false)
      commit('setCampaign', {})
      commit('setCampaignOption', {})
      commit('setRelationalCampaign', {})
      commit('setRelationalCampaignOption', {})
      commit('setAppEntryStatus', null)
      commit('setIsMobileApp', false)
      commit('setNeedsSaveHealthDataFromMobileApp', false)
      commit('setNeedsSaveHealthDataFromMobileAppRelational', false)
      commit('setWalkingPointListFromMobileApp', [])
      commit('setWeightListFromMobileApp', [])
      commit('setBloodPressureListFromMobileApp', [])
    }
  }
})

Vue.use(store)

export default store
